import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Check, Loader2 } from 'lucide-react';
import { useSchool } from '@/lib/useSchool';

/**
 * Applicant UI kit, following the OJP admissions designs:
 * a navy header band, white rounded cards, label/value rows and full-width green buttons at the bottom.
 */

export function Screen({ children }: { children: ReactNode }) {
  // Edge-to-edge on phones (cancels the layout padding), centred column on bigger screens.
  return <div className="-mx-4 -mt-4 sm:mx-0 sm:mt-0"><div className="max-w-2xl mx-auto pb-32 sm:pb-10">{children}</div></div>;
}

/** Navy header. `kicker` sits small above the title (e.g. "Step 1 of 4" or the application reference). */
export function Hero({ kicker, title, sub, children, withSchool }: { kicker?: ReactNode; title: ReactNode; sub?: ReactNode; children?: ReactNode; withSchool?: boolean }) {
  const school = useSchool();
  return (
    <div className="bg-[#0A2540] text-white px-5 sm:px-7 pt-6 pb-7 sm:rounded-2xl">
      {withSchool && (
        <div className="flex items-center gap-2.5 mb-4">
          {school.logo ? <img src={school.logo} alt="" className="w-9 h-9 rounded-lg object-cover bg-white" /> : <span className="w-9 h-9 rounded-lg bg-green-600 grid place-items-center font-extrabold">{school.name.charAt(0)}</span>}
          <span className="text-sm font-semibold text-slate-200 truncate">{school.name}</span>
        </div>
      )}
      {kicker && <p className="text-xs text-slate-300">{kicker}</p>}
      <h1 className="text-2xl sm:text-[28px] font-extrabold leading-tight">{title}</h1>
      {sub && <p className="text-sm text-slate-300 mt-1">{sub}</p>}
      {children}
    </div>
  );
}

export function Body({ children }: { children: ReactNode }) {
  return <div className="px-4 sm:px-0 pt-5 space-y-4">{children}</div>;
}

export function Panel({ children, tone, className = '' }: { children: ReactNode; tone?: 'green' | 'amber' | 'red' | 'blue'; className?: string }) {
  const t = tone === 'green' ? 'bg-green-50 border-green-100' : tone === 'amber' ? 'bg-amber-50 border-amber-100' : tone === 'red' ? 'bg-red-50 border-red-100' : tone === 'blue' ? 'bg-blue-50 border-blue-100' : 'bg-white border-slate-200';
  return <div className={`rounded-2xl border p-5 ${t} ${className}`}>{children}</div>;
}

export function Rows({ rows }: { rows: [ReactNode, ReactNode][] }) {
  return (
    <dl className="divide-y divide-slate-100">
      {rows.map(([k, v], i) => (
        <div key={i} className="flex items-center justify-between gap-4 py-3 first:pt-0 last:pb-0">
          <dt className="text-slate-500 text-[15px]">{k}</dt>
          <dd className="font-bold text-slate-900 text-right text-[15px]">{v}</dd>
        </div>
      ))}
    </dl>
  );
}

/** Sticky on phones (like the designs), inline on desktop. */
export function Actions({ children }: { children: ReactNode }) {
  return (
    <div className="fixed sm:static bottom-0 inset-x-0 z-20 bg-white/95 sm:bg-transparent backdrop-blur border-t sm:border-0 border-slate-200 px-4 py-3 sm:p-0 sm:pt-2 space-y-2">
      {children}
    </div>
  );
}

const base = 'w-full inline-flex items-center justify-center gap-2 rounded-xl py-3.5 text-[15px] font-bold transition disabled:opacity-50';
export function Primary({ to, onClick, children, disabled, busy, type = 'button' }: { to?: string; onClick?: () => void; children: ReactNode; disabled?: boolean; busy?: boolean; type?: 'button' | 'submit' }) {
  const c = `${base} bg-[#2E7D32] hover:bg-[#1B5E20] text-white`;
  const inner = <>{busy && <Loader2 className="w-4 h-4 animate-spin" />}{children}</>;
  return to ? <Link to={to} className={c}>{inner}</Link> : <button type={type} onClick={onClick} disabled={disabled || busy} className={c}>{inner}</button>;
}
export function Secondary({ to, onClick, children, disabled }: { to?: string; onClick?: () => void; children: ReactNode; disabled?: boolean }) {
  const c = `${base} border-2 border-[#2E7D32] text-[#2E7D32] bg-white hover:bg-green-50`;
  return to ? <Link to={to} className={c}>{children}</Link> : <button type="button" onClick={onClick} disabled={disabled} className={c}>{children}</button>;
}

export function Pill({ tone = 'green', children }: { tone?: 'green' | 'amber' | 'blue' | 'red' | 'slate'; children: ReactNode }) {
  const t = { green: 'bg-green-100 text-green-800', amber: 'bg-amber-100 text-amber-800', blue: 'bg-blue-100 text-blue-800', red: 'bg-red-100 text-red-700', slate: 'bg-slate-100 text-slate-600' }[tone];
  return <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold whitespace-nowrap ${t}`}>{children}</span>;
}

export const shortDate = (s?: string | null, time = false) =>
  s ? new Date(s).toLocaleString('en-GB', time ? { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' } : { day: 'numeric', month: 'short' }) : null;

export interface TrackStep { label: string; date?: string | null; state: 'done' | 'current' | 'todo'; note?: string }
/** The application tracker card from the designs. */
export function Tracker({ steps }: { steps: TrackStep[] }) {
  return (
    <ol className="space-y-4">
      {steps.map((s, i) => (
        <li key={i} className="flex items-center gap-3">
          <span className={`w-6 h-6 rounded-full grid place-items-center shrink-0 ${s.state === 'done' ? 'bg-green-600 text-white' : s.state === 'current' ? 'border-2 border-green-600 bg-white' : 'bg-slate-100'}`}>
            {s.state === 'done' ? <Check className="w-3.5 h-3.5" /> : s.state === 'current' ? <span className="w-2 h-2 rounded-full bg-green-600" /> : null}
          </span>
          <span className={`flex-1 min-w-0 ${s.state === 'todo' ? 'text-slate-400 text-[15px]' : s.state === 'current' ? 'text-green-800 font-bold text-[17px]' : 'text-slate-900 font-bold text-[17px]'}`}>
            {s.label}{s.note && <span className="block text-xs font-normal text-slate-500">{s.note}</span>}
          </span>
          {s.date && <Pill tone={s.state === 'current' ? 'amber' : 'green'}>{s.date}</Pill>}
        </li>
      ))}
    </ol>
  );
}

export function Progress({ step, of }: { step: number; of: number }) {
  return <div className="h-1.5 rounded-full bg-slate-200 overflow-hidden"><div className="h-full bg-[#2E7D32] rounded-full transition-all" style={{ width: `${(step / of) * 100}%` }} /></div>;
}

export function Field({ label, error, hint, children }: { label: string; error?: string; hint?: string; children: ReactNode }) {
  return (
    <label className="block">
      <span className="block text-[15px] font-bold text-slate-800 mb-1.5">{label}</span>
      {children}
      {error ? <span className="block text-xs text-red-600 mt-1">{error}</span> : hint ? <span className="block text-xs text-slate-500 mt-1">{hint}</span> : null}
    </label>
  );
}
export const inputBig = 'w-full rounded-xl border border-slate-200 bg-white px-4 py-3.5 text-[16px] text-slate-900 focus:outline-none focus:ring-2 focus:ring-green-600/25 focus:border-green-600 disabled:bg-slate-50';

export function Loading() {
  return <div className="py-16 grid place-items-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin" /></div>;
}
